import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeDropper, faSync, faSpinner } from "@fortawesome/free-solid-svg-icons";
import ColorPicker from "../../../Component/ColorPicker/ColorPicker";
import useFetch from "../../../Component/Hooks/useFetch";

const EditWidgetUbereats = ({ widgetId }) => {
  const user = useSelector((state) => state.userReducer);
  const userId = user.user._id;

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [widgetData, setWidgetData] = useState({
    isOnline: "online",
    type: "Widget Ubereats",
  });
  const [categories, setCategories] = useState([]);

  const { loading, data } = useFetch(
    `${process.env.REACT_APP_API_URL}/api/widgets/ubereats/getOneWidget/${widgetId}`
  );

  useEffect(() => {
    if (data) {
      setWidgetData(data.widget);
      setCategories(data.widget.categories || []);
    }
  }, [data, widgetId]);

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickers, setColorPickers] = useState({
    backgroundColor: false,
    btnColor: false,
    labelColor: false,
    borderColor: false,
  });

  const handleInputChange = (e) => {
    setWidgetData({ ...widgetData, [e.target.name]: e.target.value });
  };

  const handleColorPickerToggle = (fieldName) => {
    setColorPickers((prev) => ({
      ...Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === fieldName ? !prev[key] : false;
        return acc;
      }, {}),
    }));
    setActiveColorPicker(fieldName);
  };

  const handleColorChange = (color) => {
    setWidgetData((prev) => ({ ...prev, [activeColorPicker]: color.hex }));
  };

  /* ── Refresh from UberEats ── */
  const handleRefresh = async () => {
    if (!window.confirm(
      "Réinitialiser le menu depuis Uber Eats ?\n\nLes modifications manuelles (titres, prix, descriptions) seront écrasées et enregistrées immédiatement."
    )) return;

    setIsRefreshing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/ubereats/refresh/${widgetId}/user/${userId}`,
        { method: "POST" }
      );
      const result = await response.json();
      if (response.ok) {
        setCategories(result.categories || []);
        setWidgetData((prev) => ({
          ...prev,
          storeName: result.storeName || prev.storeName,
          storeImage: result.storeImage || prev.storeImage,
          ubereatsUrl: result.storeUrl || prev.ubereatsUrl,
          lastScrapedAt: result.lastScrapedAt || prev.lastScrapedAt,
        }));
        const itemCount = (result.categories || []).reduce(
          (acc, c) => acc + (c.items?.length || 0),
          0
        );
        toast.success(`Menu actualisé : ${itemCount} produits enregistrés.`);
      } else {
        toast.error(result.error || "Actualisation impossible.");
      }
    } catch (e) {
      toast.error("Erreur réseau lors de l'actualisation.");
    } finally {
      setIsRefreshing(false);
    }
  };

  /* ── Category / item helpers ── */
  const addCategory = () => {
    setCategories([
      ...categories,
      { name: "", order: categories.length, items: [] },
    ]);
  };

  const removeCategory = (catIdx) => {
    setCategories(categories.filter((_, i) => i !== catIdx));
  };

  const updateCategoryName = (catIdx, name) => {
    setCategories(
      categories.map((cat, i) => (i === catIdx ? { ...cat, name } : cat))
    );
  };

  const addItem = (catIdx) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              items: [
                ...(cat.items || []),
                { name: "", description: "", price: 0, imageUrl: "", productUrl: widgetData.ubereatsUrl || "", order: (cat.items || []).length },
              ],
            }
          : cat
      )
    );
  };

  const removeItem = (catIdx, itemIdx) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? { ...cat, items: (cat.items || []).filter((_, j) => j !== itemIdx) }
          : cat
      )
    );
  };

  const updateItem = (catIdx, itemIdx, field, value) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              items: (cat.items || []).map((it, j) =>
                j === itemIdx ? { ...it, [field]: value } : it
              ),
            }
          : cat
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const categoriesWithOrder = categories.map((cat, catIdx) => ({
      ...cat,
      order: catIdx,
      items: (cat.items || []).map((it, itemIdx) => ({ ...it, order: itemIdx })),
    }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/ubereats/edite/${widgetId}/user/${userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...widgetData, categories: categoriesWithOrder }),
        }
      );
      if (response.ok) {
        toast.success("Widget mis à jour.");
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || "Erreur lors de la mise à jour.");
      }
    } catch (error) {
      toast.error("Erreur réseau.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";

  const totalItems = categories.reduce((acc, c) => acc + ((c.items || []).length), 0);

  return (
    <div className="w-full max-w-2xl mx-auto py-8 px-4">
      {loading ? (
        <p className="text-center text-gray-400">Chargement...</p>
      ) : user.user.isPremium ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">

          {/* Source */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Source Uber Eats
            </p>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                URL Uber Eats
              </label>
              <input
                className={inputClass}
                type="url"
                name="ubereatsUrl"
                value={widgetData.ubereatsUrl || ""}
                onChange={handleInputChange}
              />
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="w-full p-3 rounded-xl text-sm font-semibold border-2 border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={isRefreshing ? faSpinner : faSync} spin={isRefreshing} />
              {isRefreshing ? "Actualisation..." : "Actualiser depuis Uber Eats"}
            </button>
            <p className="text-[11px] text-gray-500 text-center">
              Réimporte le menu depuis Uber Eats. Les modifications manuelles seront écrasées.
            </p>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Titre du widget *
            </label>
            <input
              className={inputClass}
              type="text"
              name="title"
              placeholder="Ex : Menu Basta Basta — Bastia"
              value={widgetData.title || ""}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Statut */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Statut
            </label>
            <select
              className={inputClass}
              name="isOnline"
              value={widgetData.isOnline || "online"}
              onChange={handleInputChange}
            >
              <option value="online">En ligne</option>
              <option value="offline">Hors ligne</option>
            </select>
          </div>

          {/* Catégories / Items */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Catalogue ({totalItems} produits)
            </p>

            <div className="flex flex-col gap-4">
              {categories.map((cat, catIdx) => (
                <div
                  key={catIdx}
                  className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
                >
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 rounded-lg border border-gray-200 p-2 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      type="text"
                      placeholder="Nom de la catégorie"
                      value={cat.name}
                      onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                    />
                    <button
                      type="button"
                      className="w-8 h-8 shrink-0 rounded-lg bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                      onClick={() => removeCategory(catIdx)}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 pl-2 border-l-2 border-gray-100">
                    {(cat.items || []).map((item, itemIdx) => (
                      <div key={itemIdx} className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          {item.imageUrl && (
                            <img src={item.imageUrl} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />
                          )}
                          <input
                            className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white"
                            type="text"
                            placeholder="Nom du produit"
                            value={item.name}
                            onChange={(e) => updateItem(catIdx, itemIdx, "name", e.target.value)}
                          />
                          <div className="relative shrink-0">
                            <input
                              className="w-[80px] rounded-md border border-gray-200 px-2 py-1.5 pr-5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-right"
                              type="number"
                              step="0.01"
                              placeholder="Prix"
                              value={item.price || 0}
                              onChange={(e) => updateItem(catIdx, itemIdx, "price", Number(e.target.value))}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                          </div>
                          <button
                            type="button"
                            className="w-7 h-7 shrink-0 rounded-md bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                            onClick={() => removeItem(catIdx, itemIdx)}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <textarea
                          className="rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white resize-none"
                          rows="2"
                          placeholder="Description"
                          value={item.description || ""}
                          onChange={(e) => updateItem(catIdx, itemIdx, "description", e.target.value)}
                        />
                      </div>
                    ))}

                    <button
                      type="button"
                      className="w-full p-2 rounded-lg text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-all"
                      onClick={() => addItem(catIdx)}
                    >
                      + Ajouter un produit
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="w-full p-2.5 rounded-lg text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-all"
              onClick={addCategory}
            >
              + Ajouter une catégorie
            </button>
          </div>

          {/* Apparence */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Apparence
            </p>
            <div className="-mx-3 flex flex-wrap">
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur de fond"
                  color={widgetData.backgroundColor || ""}
                  onColorChange={handleColorChange}
                  isOpen={colorPickers.backgroundColor}
                  togglePicker={() => handleColorPickerToggle("backgroundColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur des boutons"
                  color={widgetData.btnColor || ""}
                  onColorChange={handleColorChange}
                  isOpen={colorPickers.btnColor}
                  togglePicker={() => handleColorPickerToggle("btnColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur du texte"
                  color={widgetData.labelColor || ""}
                  onColorChange={handleColorChange}
                  isOpen={colorPickers.labelColor}
                  togglePicker={() => handleColorPickerToggle("labelColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
            </div>
            <div className="-mx-3 flex flex-wrap mt-1">
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur de bordure"
                  color={widgetData.borderColor || ""}
                  onColorChange={handleColorChange}
                  isOpen={colorPickers.borderColor}
                  togglePicker={() => handleColorPickerToggle("borderColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Contact
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
                <input
                  className={inputClass}
                  type="email"
                  name="contactMail"
                  placeholder="contact@restaurant.fr"
                  value={widgetData.contactMail || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Téléphone</label>
                <input
                  className={inputClass}
                  type="tel"
                  name="contactPhone"
                  placeholder="04 95 00 00 00"
                  value={widgetData.contactPhone || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full p-3 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
            style={{
              backgroundColor: widgetData.btnColor || "#e85a2a",
              color: widgetData.labelColor || "#ffffff",
            }}
          >
            {isLoading ? "Chargement..." : "Enregistrer les modifications"}
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default EditWidgetUbereats;
